import { useCallback, useEffect, useRef, useState } from "react";
import { type PadState, type ServerMessage } from "../shared";
import type { SyncStatus } from "../types";

const emptyState: PadState = {
  id: "public",
  text: "",
  version: 0,
  updatedAt: 0,
  files: [],
};

export function usePad(padId: string) {
  const [state, setState] = useState<PadState>({ ...emptyState, id: padId });
  const [text, setLocalText] = useState("");
  const [status, setStatus] = useState<SyncStatus>("connecting");
  const [clients, setClients] = useState(0);
  const [error, setError] = useState("");
  const [initialLoading, setInitialLoading] = useState(true);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectRef = useRef<number | null>(null);
  const debounceRef = useRef<number | null>(null);
  const retryRef = useRef(0);
  const latestTextRef = useRef("");

  const applyState = useCallback((next: PadState) => {
    setState(next);
    latestTextRef.current = next.text;
    setLocalText(next.text);
    setStatus("live");
    setError("");
    setInitialLoading(false);
  }, []);

  const fetchState = useCallback(async () => {
    const response = await fetch(`/api/pads/${encodeURIComponent(padId)}`);
    if (!response.ok) throw new Error("Could not load Slip");
    applyState((await response.json()) as PadState);
  }, [applyState, padId]);

  const connect = useCallback(() => {
    window.clearTimeout(reconnectRef.current ?? undefined);
    setStatus("connecting");

    const scheme = window.location.protocol === "https:" ? "wss" : "ws";
    const ws = new WebSocket(`${scheme}://${window.location.host}/api/pads/${encodeURIComponent(padId)}/ws`);
    wsRef.current = ws;

    ws.addEventListener("open", () => {
      retryRef.current = 0;
      setStatus("live");
      setError("");

      if (latestTextRef.current) {
        ws.send(JSON.stringify({ type: "set_text", text: latestTextRef.current }));
      }
    });

    ws.addEventListener("message", (event) => {
      const message = JSON.parse(event.data as string) as ServerMessage;
      if (message.type === "state") {
        applyState(message.state);
      }
      if (message.type === "presence") {
        setClients(message.clients);
      }
      if (message.type === "error") {
        setError(message.message);
        setStatus("error");
      }
    });

    ws.addEventListener("close", () => {
      if (wsRef.current !== ws) return;

      setStatus("offline");
      const delay = Math.min(8000, 500 * 2 ** retryRef.current);
      retryRef.current += 1;
      reconnectRef.current = window.setTimeout(connect, delay);
    });

    ws.addEventListener("error", () => {
      setStatus("error");
      setError("Connection interrupted");
    });
  }, [applyState, padId]);

  useEffect(() => {
    latestTextRef.current = "";
    setState({ ...emptyState, id: padId });
    setLocalText("");
    setInitialLoading(true);

    void fetchState().catch(() => {
      setStatus("error");
      setError("Could not load Slip");
      setInitialLoading(false);
    });

    connect();

    return () => {
      window.clearTimeout(reconnectRef.current ?? undefined);
      window.clearTimeout(debounceRef.current ?? undefined);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [connect, fetchState, padId]);

  const sendText = useCallback(
    async (value: string) => {
      latestTextRef.current = value;
      setStatus("saving");

      const ws = wsRef.current;
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "set_text", text: value }));
        return;
      }

      const response = await fetch(`/api/pads/${encodeURIComponent(padId)}/text`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ text: value }),
      });
      if (!response.ok) throw new Error("Could not save text");
      applyState((await response.json()) as PadState);
    },
    [applyState, padId],
  );

  const setText = useCallback(
    (value: string) => {
      setLocalText(value);
      latestTextRef.current = value;
      window.clearTimeout(debounceRef.current ?? undefined);
      debounceRef.current = window.setTimeout(() => {
        void sendText(value).catch(() => {
          setStatus("error");
          setError("Could not save text");
        });
      }, 250);
    },
    [sendText],
  );

  const uploadFiles = useCallback(
    async (files: File[]) => {
      for (const file of files) {
        const formData = new FormData();
        formData.set("file", file);
        const response = await fetch(`/api/pads/${encodeURIComponent(padId)}/files`, {
          method: "POST",
          body: formData,
        });

        if (!response.ok) {
          const detail = (await response.json().catch(() => null)) as { error?: string } | null;
          throw new Error(detail?.error ?? "Upload failed");
        }

        applyState((await response.json()) as PadState);
      }
    },
    [applyState, padId],
  );

  const deleteFile = useCallback(
    async (fileId: string) => {
    const response = await fetch(`/api/pads/${encodeURIComponent(padId)}/files/${encodeURIComponent(fileId)}`, {
      method: "DELETE",
    });
      if (!response.ok) throw new Error("Could not delete file");
      applyState((await response.json()) as PadState);
    },
    [applyState, padId],
  );

  const clearPad = useCallback(async () => {
    const response = await fetch(`/api/pads/${encodeURIComponent(padId)}`, { method: "DELETE" });
    if (!response.ok) throw new Error("Could not clear Slip");
    applyState((await response.json()) as PadState);
  }, [applyState, padId]);

  const reload = useCallback(() => {
    wsRef.current?.close();
    void fetchState();
    connect();
  }, [connect, fetchState]);

  return {
    state,
    text,
    setText,
    status,
    clients,
    error,
    initialLoading,
    reload,
    uploadFiles,
    deleteFile,
    clearPad,
  };
}
